import { useEffect, useEffectEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { logout } from '../services/auth/authService';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
const ACTIVITY_DEBOUNCE_MS = 1000;
export const INACTIVITY_LOGOUT_REASON = 'inactivity';

function parseInactivityMinutes(settings) {
    const raw = settings?.inactivityLogoutMinutes;
    if (raw == null || raw === '') return 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.floor(n);
}

/**
 * Logs the user out after a tenant-configured period with no user activity.
 * Tenant setting: inactivityLogoutMinutes (0 = disabled).
 */
export function useInactivityLogout() {
    const navigate = useNavigate();
    const { data: tenantSettings } = useTenantSettings();
    const minutes = parseInactivityMinutes(tenantSettings);
    const timerRef = useRef(null);
    const lastActivityResetRef = useRef(0);

    const clearTimer = useEffectEvent(() => {
        if (timerRef.current != null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    });

    const scheduleLogout = useEffectEvent(() => {
        clearTimer();
        if (minutes <= 0) return;

        timerRef.current = setTimeout(async () => {
            try {
                await logout();
            } catch {
                // Still redirect if the API call fails.
            }
            navigate('/login', { replace: true, state: { reason: INACTIVITY_LOGOUT_REASON } });
        }, minutes * 60 * 1000);
    });

    const onActivity = useEffectEvent(() => {
        const now = Date.now();
        if (now - lastActivityResetRef.current < ACTIVITY_DEBOUNCE_MS) return;
        lastActivityResetRef.current = now;
        scheduleLogout();
    });

    /* eslint-disable react-hooks/exhaustive-deps -- clearTimer/onActivity/scheduleLogout are useEffectEvents */
    useEffect(() => {
        if (minutes <= 0) {
            clearTimer();
            return undefined;
        }

        scheduleLogout();
        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, onActivity, { passive: true });
        });

        return () => {
            clearTimer();
            ACTIVITY_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, onActivity);
            });
        };
    }, [minutes]);
    /* eslint-enable react-hooks/exhaustive-deps */
}
